import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineSearch,
    HiOutlineFilter,
    HiOutlineArrowLeft,
    HiOutlineBadgeCheck,
    HiOutlineCloudUpload,
    HiOutlineClock
} from "react-icons/hi";

const CreatorCampaigns = () => {
    const [activeTab, setActiveTab] = useState("Available");
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    // Mock data based on the mockups
    const campaigns = [
        // Available / Discovery Campaigns
        { id: 101, brand: "Nike", title: "Defy Limits: Runners First", status: "Looking for Creators", deadline: "Closes in 3 days", tab: "Available", bg: "from-gray-900 to-black", payout: "$1,200", niche: "Fitness / Sport" },
        { id: 102, brand: "Olipop", title: "Summer Refresh UGC", status: "Accepting Applications", deadline: "Closes in 1 week", tab: "Available", bg: "bg-rose-50", payout: "$450 + PR Package", niche: "Lifestyle" },
        { id: 103, brand: "Sony", title: "LinkBuds S Unboxing", status: "Accepting Applications", deadline: "Closes in 5 days", tab: "Available", bg: "bg-blue-50", payout: "$850", niche: "Tech" },
        { id: 104, brand: "Glossier", title: "Cloud Paint Showcase", status: "Looking for Creators", deadline: "Closes tomorrow", tab: "Available", bg: "bg-pink-50", payout: "$600", niche: "Beauty" },
        
        // Active / History Campaigns
        { id: 1, brand: "Loreal", title: "L'Oréal Glow-Up Challenge", status: "Content Creation", deadline: "Deadline in 5 days", tab: "Active", bg: "bg-teal-50" },
        { id: 2, brand: "Adidas", title: "Summer Run Review", status: "In Review", deadline: "Deadline in 2 days", tab: "Active", bg: "bg-indigo-50" },
        { id: 3, brand: "H&M", title: "Fall Collection Try-On", status: "Approved", deadline: "Pending Payment", tab: "Pending Approval", bg: "bg-amber-50" },
        { id: 4, brand: "Sephora", title: "Holiday Makeup Look", status: "Payment Received", deadline: "Campaign Completed", tab: "Completed", bg: "bg-rose-50" },
    ];

    const filteredCampaigns = campaigns.filter(c => 
        (activeTab === "Active" && c.tab === "Active") || 
        (activeTab === "Pending Approval" && c.tab === "Pending Approval") || 
        (activeTab === "Completed" && c.tab === "Completed")
    );

    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Sidebar */}
            <Sidebar />

            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex flex-col h-screen">
                <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex flex-col md:flex-row md:justify-between md:items-center shrink-0 gap-4">
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Campaigns</h1>
                    
                    {/* Top Tabs */}
                    <div className="flex bg-gray-50/80 p-1.5 rounded-2xl border border-gray-100/50 shadow-inner overflow-x-auto hide-scrollbar">
                        {['Available', 'Active', 'Pending Approval', 'Completed'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); setSelectedCampaign(null); }}
                                className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 whitespace-nowrap ${
                                    activeTab === tab
                                        ? 'bg-[#0044CC] text-white shadow-md shadow-blue-200/50'
                                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/50'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="hidden md:flex items-center gap-3 shrink-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-[#0044CC] flex items-center justify-center text-white font-bold shadow-md ring-2 ring-white">
                            C
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 relative">
                    <div className="max-w-7xl mx-auto h-full">
                        
                        {!selectedCampaign ? (
                            <div className="animate-in fade-in duration-300">
                                <h2 className="text-xl font-extrabold text-gray-900 mb-6 tracking-tight">{activeTab} Campaigns</h2>
                                {/* Apple-Inspired Discovery Grid for 'Available' Tab */}
                                {activeTab === "Available" ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4">
                                        {filteredCampaigns.map((campaign) => (
                                            <div 
                                                key={campaign.id} 
                                                onClick={() => setSelectedCampaign(campaign)}
                                                className="bg-white p-7 rounded-[2rem] border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:scale-[1.02] transition-all duration-500 cursor-pointer flex flex-col group ring-1 ring-gray-900/5"
                                            >
                                                <div className="flex justify-between items-start mb-6">
                                                    <div className={`w-14 h-14 rounded-2xl ${campaign.bg} ring-1 ring-black/5 flex items-center justify-center text-xl font-black text-gray-900 shadow-inner select-none bg-gradient-to-br`}>
                                                        {campaign.brand.charAt(0)}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-2">
                                                        <span className="bg-gray-50 text-gray-900 px-3 py-1.5 rounded-full text-[11px] font-extrabold tracking-wide uppercase">
                                                            {campaign.niche}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <h3 className="font-extrabold text-gray-900 text-xl tracking-tight mb-2 group-hover:text-[#0044CC] transition-colors">{campaign.title}</h3>
                                                <p className="text-sm text-gray-500 font-medium mb-8 line-clamp-2 leading-relaxed">
                                                    Looking for authentic creators to showcase our products. Apply now to collaborate with {campaign.brand}.
                                                </p>

                                                <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
                                                    <div className="flex flex-col">
                                                        <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">Payout</span>
                                                        <span className="font-extrabold text-gray-900">{campaign.payout}</span>
                                                    </div>
                                                    <button className="w-10 h-10 rounded-full bg-gray-50 text-gray-900 flex items-center justify-center group-hover:bg-[#0044CC] group-hover:text-white transition-colors duration-300">
                                                        <HiOutlineArrowLeft className="rotate-180 text-lg" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* Inbox Grid View (Active, Pending, Completed) */
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredCampaigns.map((campaign) => (
                                            <div 
                                                key={campaign.id} 
                                                onClick={() => setSelectedCampaign(campaign)}
                                                className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col group"
                                            >
                                                {/* Big image placeholder top */}
                                                <div className={`w-full h-48 rounded-2xl mb-6 ${campaign.bg} group-hover:opacity-90 transition-opacity ring-1 ring-black/5`}></div>
                                                
                                                <h3 className="font-extrabold text-gray-900 text-lg mb-1">{campaign.brand}</h3>
                                                <p className="text-sm text-gray-500 font-medium mb-6">Status : {campaign.status}</p>

                                                <div className="mt-auto">
                                                    <span className={`inline-block px-4 py-2 rounded-xl text-xs font-bold ${
                                                        campaign.tab === 'Completed' 
                                                        ? 'bg-green-50 text-green-700' 
                                                        : 'bg-orange-50 text-orange-600'
                                                    }`}>
                                                        {campaign.deadline}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {filteredCampaigns.length === 0 && (
                                            <div className="col-span-full py-20 text-center text-gray-400 font-medium">
                                                No {activeTab.toLowerCase()} campaigns found.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Detailed View (Stepper) */
                            <div className="animate-in slide-in-from-right-4 fade-in duration-300 h-full">
                                <DetailedCampaignView campaign={selectedCampaign} onBack={() => setSelectedCampaign(null)} />
                            </div>
                        )}

                    </div>
                </div>
            </main>
        </div>
    );
};

const Sidebar = () => {
    const location = useLocation();
    const isActive = (path) => location.pathname === path;

    return (
        <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex shrink-0 z-20">
            <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                <span>Braccet</span>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-2">
                <SidebarItem icon={<HiOutlineHome />} label="Dashboard" to="/creator/dashboard" active={isActive('/creator/dashboard')} />
                <SidebarItem icon={<HiOutlineBriefcase />} label="Campaigns" to="/creator/find-work" active={isActive('/creator/find-work')} />
                <SidebarItem icon={<span className="font-bold text-lg">(B)</span>} label="Brands" to="/creator/brands" />
                <SidebarItem icon={<HiOutlineChatAlt2 />} label="Messages" to="/creator/messages" />
                <SidebarItem icon={<HiOutlineCog />} label="Settings" to="/creator/profile" />
            </nav>

            <div className="p-4 border-t border-gray-100">
                <button className="flex w-full items-center gap-3 p-2 rounded-xl text-gray-500 font-bold hover:bg-gray-50 hover:text-red-500 transition-colors">
                    <svg className="w-6 h-6 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Logout
                </button>
            </div>
        </aside>
    );
};

const SidebarItem = ({ icon, label, to, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

const DetailedCampaignView = ({ campaign, onBack }) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    // Calculate stepper progress based on mock status
    let currentStep = 1; // Content Creation
    if (campaign.status === 'In Review') currentStep = 2;
    if (campaign.status === 'Approved') currentStep = 4;
    if (campaign.status === 'Payment Received') currentStep = 6;

    const steps = [
        { label: "Campaign Accepted", status: currentStep > 0 ? "completed" : "current" },
        { label: "Content Creation", status: currentStep === 1 ? "current" : currentStep > 1 ? "completed" : "upcoming", desc: "You're currently working on your content. Show off your creative skills!" },
        { label: "Brand Review", status: currentStep === 2 ? "current" : currentStep > 2 ? "completed" : "upcoming" },
        { label: "Revisions", status: currentStep === 3 ? "current" : currentStep > 3 ? "completed" : "upcoming" },
        { label: "Content Approved", status: currentStep === 4 ? "current" : currentStep > 4 ? "completed" : "upcoming", desc: "Great job! Your content is approved and ready to go live." },
        { label: "Campaign Completed", status: currentStep === 5 ? "current" : currentStep > 5 ? "completed" : "upcoming" },
        { label: "Payment", status: currentStep === 6 ? "current" : "upcoming", desc: "You've successfully completed the campaign. Well done!" },
    ];

    return (
        <div className="flex flex-col xl:flex-row gap-6 lg:gap-8 h-full min-h-0 pb-4">
            {/* Left Column - Campaign Details */}
            <div className="flex-1 w-full max-w-4xl flex flex-col h-full min-h-0">
                <button onClick={onBack} className="flex shrink-0 items-center justify-center gap-2 text-gray-500 hover:text-[#0044CC] font-bold w-fit transition-colors bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm mb-4 h-9">
                    <HiOutlineArrowLeft className="text-base" /> Back to {campaign.tab}
                </button>

                <div className="bg-white rounded-[2rem] p-6 text-sm md:p-8 border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] relative flex-1 flex flex-col">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6 border-b border-gray-50 pb-4">
                        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">{campaign.brand}</h2>
                        <button className="text-gray-400 hover:text-[#0044CC] hover:bg-blue-50 p-1.5 rounded-full transition-colors">
                            <HiOutlineChatAlt2 className="text-xl" />
                        </button>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6 lg:gap-8 min-h-0 flex-1">
                        {/* Image Placeholder */}
                        <div className={`w-full md:w-48 h-48 rounded-3xl ${campaign.bg} shrink-0 ring-1 ring-black/5 shadow-inner`}></div>

                        {/* Content */}
                        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                            <h3 className="text-xl font-extrabold text-gray-900 tracking-tight mb-3">{campaign.title}</h3>
                            <p className="text-sm font-extrabold text-gray-900 mb-1.5">Description :</p>
                            <p className="text-[13px] text-gray-500 leading-relaxed mb-4 font-medium line-clamp-4">
                                Create a stunning 60-second reel showcasing L'Oréal's latest skincare sensation, Radiant Glow Serum. Highlight the product's key benefits, including its hydrating properties, instant glow, and lightweight feel. Capture the essence of natural beauty and self-care, encouraging your audience to achieve radiant skin with ease.
                            </p>

                            <div className="flex flex-wrap gap-3 items-center justify-between mt-auto pt-4">
                                <div className="flex gap-3">
                                    <button className="flex items-center gap-1.5 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg text-[13px] font-bold border border-gray-100 hover:bg-gray-100 transition-colors">
                                        <HiOutlineBadgeCheck className="text-[#0044CC] text-base" /> Script
                                    </button>
                                    <button className="flex items-center gap-1.5 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg text-[13px] font-bold border border-gray-100 hover:bg-gray-100 transition-colors">
                                        <HiOutlineBadgeCheck className="text-green-500 text-base" /> MOU
                                    </button>
                                </div>
                                <span className={`inline-block px-4 py-2 rounded-lg text-[13px] font-bold ${
                                    campaign.tab === 'Completed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-orange-50 text-orange-600 border border-orange-100'
                                }`}>
                                    {campaign.deadline}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Bottom CTA Area */}
                    <div className="mt-6 pt-6 border-t border-gray-100 flex justify-between items-center shrink-0">
                        {campaign.tab === 'Available' ? (
                             <div className="w-full flex justify-between items-center">
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500 font-medium mb-0.5">Payout</span>
                                    <span className="text-xl font-extrabold text-gray-900 tracking-tight">{campaign.payout || "$---"}</span>
                                </div>
                                <button className="px-8 py-3 bg-[#0044CC] text-white rounded-xl text-sm font-extrabold hover:bg-blue-700 transition-all duration-300 shadow-md shadow-blue-200 hover:-translate-y-0.5">
                                    Apply for Campaign
                                </button>
                             </div>
                        ) : (
                            <div className="w-full justify-end flex">
                                {campaign.status === 'Content Creation' && (
                                    <button 
                                        onClick={() => setShowUploadModal(true)}
                                        className="px-6 py-2.5 bg-green-50 text-green-600 rounded-xl text-sm font-extrabold flex items-center gap-2 hover:bg-green-100 transition-colors ring-1 ring-green-200/50 hover:shadow-md hover:shadow-green-100"
                                    >
                                        Submit for Review <HiOutlineCloudUpload className="text-lg" />
                                    </button>
                                )}
                                {campaign.status === 'In Review' && (
                                    <div className="px-6 py-2.5 bg-blue-50 text-[#0044CC] rounded-xl text-sm font-extrabold flex items-center gap-2 ring-1 ring-blue-100">
                                        In Review <HiOutlineClock className="text-lg" />
                                    </div>
                                )}
                                {campaign.status === 'Payment Received' && (
                                    <div className="px-6 py-2.5 bg-green-50 text-green-700 rounded-xl text-sm font-extrabold flex items-center gap-2 ring-1 ring-green-100">
                                        Campaign Completed 🎉
                                    </div>
                                )}
                                {campaign.status === 'Approved' && (
                                    <div className="px-6 py-2.5 bg-green-50 text-green-700 rounded-xl text-sm font-extrabold flex items-center gap-2 ring-1 ring-green-100">
                                        Content Approved
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Right Column - Status Stepper (Hide if viewing Available tab) */}
            {campaign.tab !== 'Available' && (
            <div className="w-full xl:w-72 shrink-0 flex flex-col h-[calc(100%-3rem)] min-h-0 relative mt-12">
                <h3 className="text-xl font-extrabold text-gray-900 px-2 tracking-tight shrink-0 h-9 mb-4 flex items-center">Status</h3>
                
                <div className="relative pl-3 space-y-5 flex-1 p-2">
                    {/* Vertical Line Container */}
                    <div className="absolute left-6 top-3 bottom-8 w-0 border-l-[1.5px] border-dashed border-gray-200 -translate-x-1/2"></div>

                    {steps.map((step, index) => (
                        <div key={index} className="relative flex gap-4 items-start">
                            {/* Step Indicator */}
                            <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center bg-[#FDFBF7] shrink-0 ${step.status === 'current' ? 'mt-3 lg:mt-3' : 'mt-0.5'}`}>
                                {step.status === 'completed' && (
                                    <div className="w-4 h-4 rounded-full bg-white border-[1.5px] border-green-500 flex items-center justify-center shadow-sm">
                                        <i className="fa-solid fa-check text-green-500 text-[8px] font-bold"></i>
                                    </div>
                                )}
                                {step.status === 'current' && (
                                    <div className="w-[18px] h-[18px] rounded-full bg-[#0044CC] shadow-[0_0_0_3px_rgba(0,68,204,0.15)] ring-2 ring-white"></div>
                                )}
                                {step.status === 'upcoming' && (
                                    <div className="w-4 h-4 rounded-full bg-white border-[1.5px] border-gray-300"></div>
                                )}
                            </div>

                            {/* Step Content */}
                            <div className={`flex-1 transition-all duration-300 ${
                                step.status === 'current' ? 'bg-white border border-blue-100 shadow-[0_2px_10px_-4px_rgba(0,68,204,0.1)] rounded-xl p-3 lg:p-4 -ml-1' : 
                                'h-7 flex ml-1 lg:ml-0'
                            }`}>
                                <div className={`${step.status !== 'current' ? 'h-full flex items-center' : 'flex flex-col'}`}>
                                    <h4 className={`font-extrabold text-[13px] tracking-tight ${
                                        step.status === 'current' ? 'text-[#0044CC]' : 
                                        step.status === 'completed' ? 'text-gray-900' : 
                                        'text-gray-400'
                                    }`}>{step.label}</h4>
                                    {step.desc && (step.status === 'current') && (
                                        <p className="text-[12px] text-gray-500 mt-1.5 font-medium leading-relaxed">{step.desc}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            )}

            {/* Upload Modal Overlay */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    {!uploadSuccess ? (
                        <div className="bg-white rounded-[2rem] p-8 max-w-3xl w-full shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
                            <button onClick={() => setShowUploadModal(false)} className="absolute top-6 right-6 w-8 h-8 bg-red-50 text-red-500 rounded-full flex items-center justify-center hover:bg-red-100 transition-colors">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                            
                            <h2 className="text-xl font-extrabold text-gray-900 mb-8 px-2 tracking-tight">Upload for Review</h2>
                            
                            <div className="flex flex-col md:flex-row gap-8 px-2">
                                {/* Dropzone */}
                                <div className="flex-1 bg-gray-50/50 border-2 border-dashed border-gray-200 rounded-[2rem] flex flex-col items-center justify-center p-8 text-center min-h-[250px] hover:border-[#0044CC] hover:bg-blue-50/30 transition-all duration-300 cursor-pointer group">
                                    <HiOutlineCloudUpload className="text-5xl text-gray-400 mb-4 group-hover:text-[#0044CC] group-hover:-translate-y-1 transition-all duration-300" />
                                    <p className="font-extrabold text-gray-700 text-sm mb-1">Drop video to upload</p>
                                    <p className="text-sm text-gray-400 font-medium">or <span className="text-[#0044CC] font-bold hover:underline">Browse</span></p>
                                </div>
                                
                                {/* Form */}
                                <div className="flex-1 flex flex-col gap-5">
                                    <div>
                                        <label className="block text-sm font-extrabold text-gray-900 mb-2">Name</label>
                                        <input type="text" defaultValue={campaign.title + " #1"} className="w-full px-5 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0044CC]/20 focus:border-[#0044CC] text-sm text-gray-700 font-medium transition-shadow" />
                                    </div>
                                    <div className="flex-1 flex flex-col">
                                        <label className="block text-sm font-extrabold text-gray-900 mb-2">Description</label>
                                        <textarea defaultValue="Lorem Ipsum is simply dummy text of the printing and typesetting industry." className="w-full flex-1 px-5 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0044CC]/20 focus:border-[#0044CC] text-sm text-gray-700 font-medium resize-none transition-shadow"></textarea>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-8 pt-4 flex justify-end px-2">
                                <button onClick={() => setUploadSuccess(true)} className="px-10 py-3.5 bg-[#0044CC] text-white rounded-2xl font-extrabold hover:bg-blue-700 transition-all duration-300 shadow-lg shadow-blue-200 hover:-translate-y-0.5">
                                    Submit
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Success State */
                        <div className="bg-white rounded-[2rem] p-12 max-w-lg w-full shadow-2xl relative text-center flex flex-col items-center animate-in fade-in zoom-in-95 duration-200">
                            <button onClick={() => { setShowUploadModal(false); setUploadSuccess(false); onBack(); }} className="absolute top-6 right-6 w-8 h-8 bg-red-50 text-red-500 rounded-full flex items-center justify-center hover:bg-red-100 transition-colors">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                            
                            {/* Abstract animation rings */}
                            <div className="w-40 h-40 relative mb-8 flex items-center justify-center">
                                <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-20"></div>
                                <div className="absolute inset-4 bg-blue-50/80 rounded-full"></div>
                                <div className="absolute inset-8 bg-blue-100/60 rounded-full"></div>
                                <div className="relative w-[4.5rem] h-[4.5rem] bg-[#0044CC] rounded-full flex items-center justify-center text-white text-3xl shadow-[0_8px_30px_rgb(0,68,204,0.3)]">
                                    <i className="fa-solid fa-check"></i>
                                </div>
                                {/* Scattered dots (mocking the mockup) */}
                                <div className="absolute top-2 left-6 w-3.5 h-3.5 bg-[#0044CC] rounded-full"></div>
                                <div className="absolute bottom-4 right-6 w-4 h-4 bg-blue-400 rounded-full"></div>
                                <div className="absolute top-8 right-2 w-2.5 h-2.5 bg-blue-300 rounded-full"></div>
                                <div className="absolute bottom-6 left-4 w-2.5 h-2.5 bg-blue-300 rounded-full"></div>
                                <div className="absolute top-1/2 -right-4 w-3.5 h-3.5 bg-[#0044CC] rounded-full"></div>
                            </div>

                            <h2 className="text-2xl font-extrabold text-gray-900 mb-6 tracking-tight">Video Uploaded Successfully</h2>
                            <div className="px-6 py-2.5 border border-gray-200 text-[#0044CC] rounded-full text-sm font-extrabold bg-white shadow-sm inline-block">
                                In Review
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CreatorCampaigns;
